// components/ChallengeLoader/ChallengeLoader.jsx
//
// The animated loader shown while a challenge is prepared. Purely
// presentational and never in the way:
//   - muted, looping, inline autoplay, no controls, no audio ever;
//   - the `fallback` (the page's normal spinner) shows until the video is
//     actually playing, and again if it fails or autoplay is blocked;
//   - it plays for as long as it stays mounted — the parent unmounts it the
//     moment the challenge is ready, so the video length never decides how
//     long loading takes;
//   - prefers-reduced-motion keeps the standard spinner.
// Caching: loader URLs are immutable (a replaced video gets a new URL) and
// served with a one-year Cache-Control, so the browser cache reuses them.
import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import styles from './ChallengeLoader.module.css';

const joinSlugs = (list) => (Array.isArray(list) ? list.slice(0, 10).join(',') : '');

export default function ChallengeLoader({ loader, mediaCategories = [], fallback = null }) {
    const reducedMotion = useReducedMotion();
    const [status, setStatus] = useState('loading'); // loading | playing | failed
    const videoRef = useRef(null);
    const reportedRef = useRef(false);

    const videoUrl = loader?.videoUrl || null;
    useEffect(() => { setStatus('loading'); reportedRef.current = false; }, [videoUrl]);

    const shouldPlay = Boolean(videoUrl) && !reducedMotion && status !== 'failed';

    const fail = (reason) => {
        setStatus('failed');
        if (reportedRef.current) return;
        reportedRef.current = true;
        trackEvent(ANALYTICS_EVENTS.CHALLENGE_LOADER_FAILED, { loader_id: loader?.id, reason, platform: 'web' });
    };

    // Some browsers only honour `muted` for autoplay when set as a property.
    useEffect(() => {
        const el = videoRef.current;
        if (!shouldPlay || !el) return;
        el.muted = true;
        el.defaultMuted = true;
        el.volume = 0;
        const attempt = el.play?.();
        if (attempt?.catch) attempt.catch(() => fail('autoplay_blocked'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldPlay, videoUrl]);

    const handlePlaying = () => {
        if (status === 'playing') return; // `playing` can fire again on loop/rebuffer
        setStatus('playing');
        if (reportedRef.current) return;
        reportedRef.current = true;
        trackEvent(ANALYTICS_EVENTS.CHALLENGE_LOADER_SHOWN, {
            loader_id: loader.id,
            loader_categories: joinSlugs(loader.categories),
            media_genres: joinSlugs(mediaCategories),
            platform: 'web',
        });
    };

    if (!shouldPlay) return fallback;

    return (
        <div className={styles.frame} data-testid="challenge-loader" data-status={status}>
            <video
                ref={videoRef}
                className={`${styles.video} ${status === 'playing' ? styles.visible : ''}`}
                src={videoUrl}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                controls={false}
                disablePictureInPicture
                aria-hidden="true"
                data-testid="challenge-loader-video"
                onPlaying={handlePlaying}
                onError={() => fail('playback_error')}
            />
            {status !== 'playing' && <div className={styles.fallback}>{fallback}</div>}
        </div>
    );
}
